import React, { useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { ShieldCheck, LayoutDashboard, LogOut, ExternalLink, Target } from "lucide-react";
import { useAuth } from "@/lib/auth";
import Logo from "./Logo";
import PageTransition from "./PageTransition";
import SiteFooter from "./SiteFooter";
import ThemedBackground from "./ThemedBackground";
import { API } from "@/lib/api";
import { resetThemeToStock } from "@/lib/themes";
import LogoSplash from "./LogoSplash";

export default function OwnerShell({ children }) {
  const { user, logout } = useAuth();

  // El panel del creador SIEMPRE usa colores stock de ZAVIA (el tema es solo para la app del colegio).
  useEffect(() => { resetThemeToStock(); }, []);

  return (
    <div className="min-h-screen bg-navy-ink text-white flex flex-col relative">
      <LogoSplash sessionKey="owner" />
      <ThemedBackground bgId="exec-navy" />
      <div className="h-[3px] w-full bg-navy-light relative z-10" />
      <header className="border-b border-white/10 bg-navy-ink/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo variant="light" />
            <div className="hidden md:flex items-center gap-1">
              <NavLink end to="/admin" data-testid="nav-admin-panel" className={({isActive}) => `px-3 py-2 text-xs uppercase tracking-[0.25em] transition-colors ${isActive ? "text-white border-b border-white" : "text-nardo-300 hover:text-white"}`}>
                Panel del creador
              </NavLink>
              <NavLink to="/admin/objetivos" data-testid="nav-admin-objectives" className={({isActive}) => `px-3 py-2 text-xs uppercase tracking-[0.25em] transition-colors inline-flex items-center gap-1.5 ${isActive ? "text-white border-b border-white" : "text-nardo-300 hover:text-white"}`}>
                <Target className="w-3 h-3" /> Objetivos
              </NavLink>
              <Link to="/dashboard" data-testid="nav-app-view" className="px-3 py-2 text-xs uppercase tracking-[0.25em] text-nardo-300 hover:text-white inline-flex items-center gap-1.5">
                Ver app de usuario <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3">
              <ShieldCheck className="w-4 h-4 text-emerald-300" />
              <div className="text-right">
                <div className="text-sm font-medium">{user?.name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">Propietario</div>
              </div>
            </div>
            <button data-testid="owner-logout" onClick={logout} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-nardo-300 hover:text-white border border-white/20 hover:border-white px-3 py-2 rounded-md">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
        <div className="md:hidden border-t border-white/10 px-4 py-2 flex items-center gap-1 overflow-x-auto">
          <NavLink end to="/admin" data-testid="nav-admin-panel-mobile" className={({isActive}) => `whitespace-nowrap px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] rounded-md transition-colors ${isActive ? "bg-white/10 text-white" : "text-nardo-300 hover:text-white"}`}>
            Panel del creador
          </NavLink>
          <NavLink to="/admin/objetivos" data-testid="nav-admin-objectives-mobile" className={({isActive}) => `whitespace-nowrap px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] rounded-md transition-colors inline-flex items-center gap-1 ${isActive ? "bg-white/10 text-white" : "text-nardo-300 hover:text-white"}`}>
            <Target className="w-3 h-3" /> Objetivos
          </NavLink>
          <Link to="/dashboard" data-testid="nav-app-view-mobile" className="whitespace-nowrap px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] rounded-md text-nardo-300 hover:text-white inline-flex items-center gap-1.5">
            Ver app de usuario <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 sm:py-12 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white text-foreground rounded-lg p-6 sm:p-10 shadow-2xl">
            <PageTransition>{children}</PageTransition>
          </div>
          <SiteFooter variant="dark" />
        </div>
      </main>

      <footer className="border-t border-white/10 px-6 lg:px-10 py-6 text-center text-xs text-nardo-400 uppercase tracking-[0.3em]">
        ZAVIA · Panel del creador
      </footer>
    </div>
  );
}
